import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	Col,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	Row,
	UncontrolledDropdown,
} from 'reactstrap';

import { messages } from '../../i18n/messages';

import Button from '../Button';

import styles from './PaginationToolbar.module.scss';

const SIZE_PER_PAGE_LIST = [10, 25, 50, 100];
const PAGE_RANGE_SIZE = 3;

const propTypes = {

	/**
	 * The number of items to display per page.
	 */
	pageSize: PropTypes.number.isRequired,

	/**
	 * The current page index.
	 */
	pageIndex: PropTypes.number.isRequired,

	/**
	 * The total number of pages to display.
	 */
	pageCount: PropTypes.number.isRequired,

	/**
	 * Flag indicating whether there is another page to display.
	 */
	hasNext: PropTypes.bool.isRequired,

	/**
	 * Flag indicating whether there is a preceding page to display.
	 */
	hasPrevious: PropTypes.bool.isRequired,

	/**
	 * List of page size options to display.
	 */
	pageSizeOptions: PropTypes.arrayOf(PropTypes.number),

	/**
	 * Callback for selecting the table page size.
	 */
	onSelectPageSize: PropTypes.func.isRequired,

	/**
	 * Callback to jump to a given page.
	 */
	gotoPage: PropTypes.func.isRequired,

};

const PaginationToolbar = (props) => {

	const {
		pageSize,
		pageIndex,
		pageCount,
		hasNext,
		hasPrevious,
		pageSizeOptions = SIZE_PER_PAGE_LIST,
		gotoPage,
		onSelectPageSize,
	} = props;

	const intl = useIntl();

	const pageNumber = pageIndex + 1;

	// Determine button groupings for page navigation
	let startPageRange = [], midPageRange = [], endPageRange = [];

	// If less than (PAGE_RANGE_SIZE * 2) pages total, show all page buttons
	if (pageCount < (PAGE_RANGE_SIZE * 2)) {

		for (let i = 1; i <= pageCount; i++) {
			startPageRange.push(i);
		}

	// Otherwise show abbreviated ranges
	} else {

		// If we're near the beginning, show first group of page buttons
		if (pageNumber <= PAGE_RANGE_SIZE) {

			// Show options for the first few pages
			for (let i = 1; i <= PAGE_RANGE_SIZE; i++) {
				startPageRange.push(i);
			}

			// If currently at end of range, add additional page button
			if (pageNumber === PAGE_RANGE_SIZE && pageCount > pageNumber) {
				startPageRange.push(pageNumber + 1);
			}

		// Otherwise only link to the first page
		} else {

			startPageRange = [1];

		}

		// If we're in the middle, show range around current page number
		if ((pageNumber > PAGE_RANGE_SIZE) && (pageNumber <= (pageCount - PAGE_RANGE_SIZE))) {
			midPageRange = [pageNumber - 1, pageNumber, pageNumber + 1];
		}

		// If we're near the end, show last group of page buttons
		if (pageNumber > (pageCount - PAGE_RANGE_SIZE)) {

			// Show options for the last few pages
			for (let i = (PAGE_RANGE_SIZE - 1); i >= 0; i--) {
				endPageRange.push(pageCount - i);
			}

			// If currently at beginning of range, add additional page button
			if (pageNumber === (pageCount - PAGE_RANGE_SIZE + 1)) {
				endPageRange.unshift(pageCount - PAGE_RANGE_SIZE);
			}

		// Otherwise only link to the last page
		} else {
			endPageRange = [pageCount];
		}

	}

	return (
		<Row className="tc-table-pagination-toolbar">
			<Col
				className="page-size-dropdown-container"
				xs="6">
				{pageSizeOptions.length > 0 &&
					<div className="d-flex align-items-center">
						<span>
							<FormattedMessage
								{...messages.TC_COMPONENTS_TABLE_PAGINATION_TOOLBAR_PAGE_SIZE_DROPDOWN_LABEL} />
						</span>
						<UncontrolledDropdown className="page-size-dropdown ms-2">
							<DropdownToggle
								className="px-2 py-1"
								color="neutral"
								aria-label={`${intl.formatMessage(messages.TC_COMPONENTS_TABLE_PAGINATION_TOOLBAR_PAGE_SIZE_DROPDOWN_LABEL)} ${pageSize}`}
								outline>
								{pageSize}
								<i
									className="fas fa-caret-down ms-2"
									aria-hidden="true" />
							</DropdownToggle>
							<DropdownMenu>
								{pageSizeOptions.map((pageSizeOption) => (
									<DropdownItem
										key={pageSizeOption}
										active={pageSize === pageSizeOption}
										onClick={() => onSelectPageSize(pageSizeOption)}>
										{pageSizeOption}
									</DropdownItem>
								))}
							</DropdownMenu>
						</UncontrolledDropdown>
					</div>
				}
			</Col>
			<Col
				className={`page-range-buttons ${styles['nav-btns']}`}
				xs="6">
				<Button
					color="neutral"
					disabled={!hasPrevious}
					outline
					onClick={() => gotoPage(pageIndex - 1)}>
					<FormattedMessage
						{...messages.TC_COMPONENTS_GENERAL_TEXT_BACK} />
				</Button>
				{startPageRange.map((page) => (
					<Button
						key={`page-range-btn-${page}`}
						color={pageIndex === (page - 1) ? 'primary' : 'neutral'}
						outline={pageIndex !== (page - 1)}
						onClick={() => gotoPage(page - 1)}>
						{page}
					</Button>
				))}
				{midPageRange.length > 0 &&
					<span className={styles['nav-ellipsis']}>
						{'...'}
					</span>
				}
				{midPageRange.map((page) => (
					<Button
						key={`page-range-btn-${page}`}
						color={pageIndex === (page - 1) ? 'primary' : 'neutral'}
						outline={pageIndex !== (page - 1)}
						onClick={() => gotoPage(page - 1)}>
						{page}
					</Button>
				))}
				{endPageRange.length > 0 &&
					<span className={styles['nav-ellipsis']}>
						{'...'}
					</span>
				}
				{endPageRange.map((page) => (
					<Button
						key={`page-range-btn-${page}`}
						color={pageIndex === (page - 1) ? 'primary' : 'neutral'}
						outline={pageIndex !== (page - 1)}
						onClick={() => gotoPage(page - 1)}>
						{page}
					</Button>
				))}
				<Button
					color="neutral"
					disabled={!hasNext}
					outline
					onClick={() => gotoPage(pageIndex + 1)}>
					<FormattedMessage
						{...messages.TC_COMPONENTS_GENERAL_TEXT_NEXT} />
				</Button>
			</Col>
		</Row>
	);

};

PaginationToolbar.propTypes = propTypes;

export default PaginationToolbar;
